import { useCurrency, type Prices } from '@/lib/currency'
import { useTranslation } from '@/lib/i18n'

interface Props {
  /** The line the piece belongs to, or null for a loose piece. */
  category: string | null
  /** The fallback when the piece has no line: its build. */
  material: string | null
  price: number
  /** The piece's own tags by currency, when it has any. */
  prices?: Prices
}

/**
 * The line under a glass card's render, off the "Home Hero Variants"
 * sheet's 1c strip: what the piece is, its price opposite — both in
 * full ink. It replaces the "view product" button the cards used to
 * raise on hover — the price is what a lead is looking for, and it
 * shouldn't take a pointer to find it. A phone has no hover at all.
 *
 * The line rather than the material, which is the sheet's short "Cricket
 * lighter" rather than a build note long enough to run out of card.
 */
export default function CardMeta({ category, material, price, prices }: Props) {
  const { t } = useTranslation()
  const { format: formatPrice } = useCurrency()

  // `t` echoes a key it doesn't know, which would put "home.category.x"
  // on the card; a line without a label falls back to the build.
  const key = `home.category.${category}`
  const label = category ? t(key) : null
  const sub = label && label !== key ? label : material

  // Not a link of its own: the name and the render above it already go
  // to the piece, and a third copy would only pad the tab order.
  return (
    <div className="relative flex items-baseline gap-3 px-1 text-[13px] text-ink">
      {sub && <span className="truncate">{sub}</span>}
      <span className="ml-auto shrink-0 font-semibold tabular-nums">
        {formatPrice(price, prices)}
      </span>
    </div>
  )
}
